import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  UserPlus, 
  Users, 
  School, 
  Loader2, 
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  CheckCircle,
  XCircle,
  UserMinus,
  Upload,
  FileSpreadsheet,
  Download,
  RefreshCw,
  FileText,
  AlertTriangle,
  UserCheck,
  Clock,
  Mail,
  Phone,
  MapPin,
  Building
} from 'lucide-react';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherManagementContent = () => {
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [expandedTeacher, setExpandedTeacher] = useState(null);
  const [teacherStudents, setTeacherStudents] = useState({});
  
  // Add teacher dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addingTeacher, setAddingTeacher] = useState(false);
  
  // Assign students dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTeacherId, setAssignTeacherId] = useState('');
  const [assignSchoolId, setAssignSchoolId] = useState('');
  const [studentsForAssignment, setStudentsForAssignment] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Bulk Import State
  const [bulkSelectedSchool, setBulkSelectedSchool] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false); // Toggle for replace vs append mode

  // User Approvals State
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [selectedPendingUsers, setSelectedPendingUsers] = useState([]);
  const [processingUserIds, setProcessingUserIds] = useState([]);
  const [approvalSearchTerm, setApprovalSearchTerm] = useState('');

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers`);
      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast.error('Failed to load teachers');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchSchools = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schools`);
      if (response.ok) {
        const data = await response.json();
        setSchools(data || []);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  }, []);

  // Fetch pending user approvals
  const fetchPendingApprovals = useCallback(async () => {
    setLoadingApprovals(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pending-approvals`);
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.pending_users || []);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    } finally {
      setLoadingApprovals(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTeachers(), fetchUsers(), fetchSchools(), fetchPendingApprovals()]);
      setLoading(false);
    };
    loadData();
    
    // Refetch when page becomes visible or gains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    
    const handleFocus = () => {
      loadData();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchTeachers, fetchUsers, fetchSchools, fetchPendingApprovals]);

  const fetchTeacherStudents = async (teacherId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers/${teacherId}/students`);
      if (response.ok) {
        const data = await response.json();
        setTeacherStudents(prev => ({
          ...prev,
          [teacherId]: data.students || []
        }));
      }
    } catch (error) {
      console.error('Error fetching teacher students:', error);
    }
  };

  const toggleTeacherExpand = async (teacherId) => {
    if (expandedTeacher === teacherId) {
      setExpandedTeacher(null);
    } else {
      setExpandedTeacher(teacherId);
      if (!teacherStudents[teacherId]) {
        await fetchTeacherStudents(teacherId);
      }
    }
  };

  const handleAddTeacher = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setAddingTeacher(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUserId, schools: [] })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setShowAddDialog(false);
        setSelectedUserId('');
        fetchTeachers();
      } else {
        toast.error(data.detail || 'Failed to add teacher');
      }
    } catch (error) {
      toast.error('Failed to add teacher');
    } finally {
      setAddingTeacher(false);
    }
  };

  const handleRemoveTeacher = async (teacherId, teacherName) => {
    if (!window.confirm(`Remove ${teacherName} as a teacher? This will also remove all student assignments.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers/${teacherId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Teacher removed successfully');
        fetchTeachers();
      } else {
        toast.error('Failed to remove teacher');
      }
    } catch (error) {
      toast.error('Failed to remove teacher');
    }
  };

  const fetchStudentsForAssignment = async (schoolId) => {
    if (!schoolId) return;
    
    setLoadingStudents(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/students-for-assignment?school_id=${schoolId}`);
      if (response.ok) {
        const data = await response.json();
        setStudentsForAssignment(data.students || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const openAssignDialog = (teacherId) => {
    setAssignTeacherId(teacherId);
    setAssignSchoolId('');
    setStudentsForAssignment([]);
    setSelectedStudents([]);
    setShowAssignDialog(true);
  };

  const handleSchoolChange = (schoolId) => {
    setAssignSchoolId(schoolId);
    setSelectedStudents([]);
    fetchStudentsForAssignment(schoolId);
  };

  const toggleStudentSelection = (studentId) => {
    // Only allow toggling students that are NOT assigned to any teacher
    const student = studentsForAssignment.find(s => s.id === studentId);
    if (student?.assigned_teacher_id) return;
    
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    const availableStudentIds = filteredStudents
      .filter(s => !s.assigned_teacher_id)
      .map(s => s.id);
    setSelectedStudents(availableStudentIds);
  };

  const handleAssignStudents = async () => {
    // Double-check: only send truly available (unassigned) students
    const safeStudentIds = selectedStudents.filter(id => {
      const student = studentsForAssignment.find(s => s.id === id);
      return student && !student.assigned_teacher_id;
    });

    if (safeStudentIds.length === 0) {
      toast.error('Please select at least one available student');
      return;
    }

    setAssigning(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teacher-assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: assignTeacherId,
          student_ids: safeStudentIds,
          school_id: assignSchoolId
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setShowAssignDialog(false);
        fetchTeachers();
        // Refresh teacher students if expanded
        if (expandedTeacher === assignTeacherId) {
          fetchTeacherStudents(assignTeacherId);
        }
      } else {
        toast.error(data.detail || 'Failed to assign students');
      }
    } catch (error) {
      toast.error('Failed to assign students');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (studentId, schoolId, studentName) => {
    if (!window.confirm(`Are you sure you want to unassign ${studentName || 'this student'} from the teacher?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teacher-assignments/${studentId}/${schoolId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Student unassigned successfully');
        if (expandedTeacher) {
          fetchTeacherStudents(expandedTeacher);
        }
        fetchTeachers();
      } else {
        toast.error('Failed to unassign student');
      }
    } catch (error) {
      toast.error('Failed to unassign student');
    }
  };

  // Filter users who are not already teachers
  const availableUsers = users.filter(u => 
    !teachers.some(t => t.user_id === u.id)
  );

  // Filter students for assignment
  const filteredStudents = studentsForAssignment.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ==================== BULK IMPORT HANDLERS ====================
  
  // File drop handler for bulk import
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(uploadedFile.type) && !uploadedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setBulkFile(uploadedFile);
    setParseError(null);
    setImportResult(null);
    setIsParsing(true);

    // Parse file for preview
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const response = await fetch(`${BACKEND_URL}/api/admin/parse-student-file`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.students || []);
        toast.success(`Found ${data.total} students in file`);
      } else {
        const error = await response.json();
        setParseError(error.detail || 'Failed to parse file');
        toast.error('Failed to parse file');
      }
    } catch (error) {
      setParseError('Error reading file');
      toast.error('Error reading file');
    } finally {
      setIsParsing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1
  });

  // Handle bulk import
  const handleBulkImport = async () => {
    if (!bulkFile || !bulkSelectedSchool) {
      toast.error('Please select a school and upload a file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setImportResult(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Choose endpoint based on replace mode
      const endpoint = replaceMode 
        ? `${BACKEND_URL}/api/admin/bulk-replace-enrollment`
        : `${BACKEND_URL}/api/admin/bulk-enroll`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: bulkSelectedSchool,
          students: previewData,
          default_password: 'Laikos2024'
        })
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        const successMsg = replaceMode 
          ? `Successfully replaced enrollments! ${result.total_enrolled || 0} students enrolled.`
          : `Successfully imported ${result.enrolled_in_school || 0} students!`;
        toast.success(successMsg);
        // Refresh users list
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
        setImportResult({ error: error.detail || 'Import failed' });
      }
    } catch (error) {
      toast.error('Import failed: ' + error.message);
      setImportResult({ error: error.message });
    } finally {
      setIsUploading(false);
    }
  };

  // Reset bulk import form
  const handleBulkReset = () => {
    setBulkFile(null);
    setPreviewData([]);
    setImportResult(null);
    setParseError(null);
    setUploadProgress(0);
  };

  // Download template
  const downloadTemplate = () => {
    const csvContent = "Name,Email,Phone,Branch,Constituency\nJohn Doe,john@example.com,+1234567890,Newark,NEW JERSEY/NEW YORK\nJane Smith,jane@example.com,+0987654321,Boston,NEW ENGLAND";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded!');
  };

  // ==================== USER APPROVAL HANDLERS ====================
  
  const handleApproveUser = async (userId) => {
    setProcessingUserIds(prev => [...prev, userId]);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/approve-user/${userId}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('User approved successfully');
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
        setSelectedPendingUsers(prev => prev.filter(id => id !== userId));
        fetchUsers(); // Refresh users list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve user');
      }
    } catch (error) {
      toast.error('Error approving user');
    } finally {
      setProcessingUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleRejectUser = async (userId) => {
    setProcessingUserIds(prev => [...prev, userId]);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reject-user/${userId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`User rejected and removed from system`);
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
        setSelectedPendingUsers(prev => prev.filter(id => id !== userId));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject user');
      }
    } catch (error) {
      toast.error('Error rejecting user');
    } finally {
      setProcessingUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPendingUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    setProcessingUserIds(selectedPendingUsers);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selectedPendingUsers)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved ${data.approved_count} users`);
        setPendingUsers(prev => prev.filter(u => !selectedPendingUsers.includes(u.id)));
        setSelectedPendingUsers([]);
        fetchUsers();
      } else {
        toast.error('Failed to bulk approve users');
      }
    } catch (error) {
      toast.error('Error during bulk approval');
    } finally {
      setProcessingUserIds([]);
    }
  };

  const toggleSelectPendingUser = (userId) => {
    setSelectedPendingUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredPendingUsers = pendingUsers.filter(user => {
    const search = approvalSearchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.branch?.toLowerCase().includes(search) ||
      user.constituency?.toLowerCase().includes(search)
    );
  });

  const toggleSelectAllPending = () => {
    if (selectedPendingUsers.length === filteredPendingUsers.length) {
      setSelectedPendingUsers([]);
    } else {
      setSelectedPendingUsers(filteredPendingUsers.map(u => u.id));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#5f7c8a]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8 pt-20">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5f7c8a] hover:text-[#4a6470] mb-6"
        >
          <ArrowLeft size={20} />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Teacher & Student Management</h1>
          <p className="text-gray-600 mt-1">Manage teachers, assign students, and bulk import</p>
        </div>

        {/* Tabs for Teachers, Bulk Import, and Approvals */}
        <Tabs defaultValue="teachers" className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="teachers" className="flex items-center gap-2" data-testid="teachers-tab">
              <Users size={16} />
              Teachers
            </TabsTrigger>
            <TabsTrigger value="bulk-import" className="flex items-center gap-2" data-testid="bulk-import-tab">
              <Upload size={16} />
              Bulk Import
            </TabsTrigger>
            <TabsTrigger value="approvals" className="flex items-center gap-2" data-testid="approvals-tab">
              <UserCheck size={16} />
              Approvals
              {pendingUsers.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {pendingUsers.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Teachers Tab */}
          <TabsContent value="teachers" className="space-y-6">
            {/* Add Teacher Button */}
            <div className="flex justify-end">
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#5f7c8a] hover:bg-[#4a6470]" data-testid="add-teacher-btn">
                    <UserPlus size={18} className="mr-2" />
                    Add Teacher
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Teacher</DialogTitle>
                    <DialogDescription>
                      Select an existing user to designate as a teacher
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Select User</Label>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger data-testid="user-select">
                          <SelectValue placeholder="Choose a user" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUsers.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleAddTeacher} 
                      disabled={addingTeacher || !selectedUserId}
                      className="w-full"
                    >
                      {addingTeacher ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                      Add as Teacher
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Teachers List */}
            <div className="space-y-4">
              {teachers.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-500">No teachers added yet</p>
                    <p className="text-sm text-gray-400 mt-1">Click "Add Teacher" to get started</p>
                  </CardContent>
                </Card>
              ) : (
                teachers.map(teacher => (
                  <Card key={teacher.user_id} className="overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#5f7c8a]/10 to-transparent">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[#5f7c8a] rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {teacher.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{teacher.name}</CardTitle>
                            <CardDescription>{teacher.email}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge variant="secondary" className="gap-1">
                            <Users size={14} />
                            {teacher.assignment_count || 0} Students
                          </Badge>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openAssignDialog(teacher.user_id)}
                            data-testid={`assign-students-${teacher.user_id}`}
                          >
                            <UserPlus size={16} className="mr-1" />
                            Assign Students
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleTeacherExpand(teacher.user_id)}
                          >
                            {expandedTeacher === teacher.user_id ? (
                              <ChevronUp size={18} />
                            ) : (
                              <ChevronDown size={18} />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveTeacher(teacher.user_id, teacher.name)}
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedTeacher === teacher.user_id && (
                      <CardContent className="pt-4">
                        <h4 className="font-medium text-gray-700 mb-3">Assigned Students</h4>
                        {teacherStudents[teacher.user_id]?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Student Name</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>School</TableHead>
                                  <TableHead>Branch</TableHead>
                                  <TableHead className="w-28 text-center">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {teacherStudents[teacher.user_id].map(student => (
                                  <TableRow key={`${student.student_id}-${student.school_id}`}>
                                    <TableCell className="font-medium">{student.student_name}</TableCell>
                                    <TableCell>{student.student_email}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline">{student.school_name}</Badge>
                                    </TableCell>
                                    <TableCell>{student.student_branch}</TableCell>
                                    <TableCell className="text-center">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                                        onClick={() => handleRemoveAssignment(student.student_id, student.school_id, student.student_name)}
                                        data-testid={`unassign-${student.student_id}`}
                                      >
                                        <UserMinus size={14} className="mr-1" />
                                        Unassign
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm py-4">No students assigned yet</p>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Bulk Import Tab */}
          <TabsContent value="bulk-import" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Upload Area */}
              <div className="lg:col-span-2 space-y-6">
                {/* School Selection */}
                <Card className="border-2 border-blue-200">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <School className="text-blue-500" />
                      Step 1: Select School
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <select
                      value={bulkSelectedSchool}
                      onChange={(e) => setBulkSelectedSchool(e.target.value)}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="bulk-school-select"
                    >
                      <option value="">-- Select a school --</option>
                      {schools.map(school => (
                        <option key={school.id} value={school.id}>
                          {school.name} {school.language ? `(${school.language === 'en' ? 'English' : 'French'})` : ''}
                        </option>
                      ))}
                    </select>
                  </CardContent>
                </Card>

                {/* File Upload Area */}
                <Card className="border-2 border-green-200">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Upload className="text-green-500" />
                      Step 2: Upload Student File
                    </CardTitle>
                    <CardDescription>
                      Drag & drop or click to upload Excel (.xlsx) or CSV file
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                        ${isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-green-50/50'}
                        ${bulkFile ? 'border-green-500 bg-green-50' : ''}`}
                    >
                      <input {...getInputProps()} data-testid="bulk-file-input" />
                      
                      {isParsing ? (
                        <div className="space-y-3">
                          <Loader2 size={48} className="mx-auto text-green-500 animate-spin" />
                          <p className="font-medium text-gray-800">Parsing file...</p>
                        </div>
                      ) : bulkFile ? (
                        <div className="space-y-3">
                          <FileSpreadsheet size={48} className="mx-auto text-green-500" />
                          <p className="font-medium text-gray-800">{bulkFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(bulkFile.size / 1024).toFixed(1)} KB
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); handleBulkReset(); }}
                            className="text-red-500 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 size={14} className="mr-1" /> Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload size={48} className="mx-auto text-gray-400" />
                          <p className="text-gray-600">
                            {isDragActive ? 'Drop the file here...' : 'Drag & drop file here, or click to browse'}
                          </p>
                          <p className="text-sm text-gray-400">
                            Supports .xlsx, .xls, .csv files
                          </p>
                        </div>
                      )}
                    </div>

                    {parseError && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <AlertTriangle size={18} />
                        {parseError}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Preview Table */}
                {previewData.length > 0 && (
                  <Card className="border-2 border-purple-200">
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="text-purple-500" />
                        Step 3: Preview ({previewData.length} students found)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="overflow-x-auto max-h-[300px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">#</TableHead>
                              <TableHead className="text-xs">Name</TableHead>
                              <TableHead className="text-xs">Email</TableHead>
                              <TableHead className="text-xs">Phone</TableHead>
                              <TableHead className="text-xs">Branch</TableHead>
                              <TableHead className="text-xs">Constituency</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {previewData.slice(0, 20).map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-xs text-gray-500">{index + 1}</TableCell>
                                <TableCell className="text-xs font-medium">{row.name}</TableCell>
                                <TableCell className="text-xs">{row.email}</TableCell>
                                <TableCell className="text-xs">{row.phone || '-'}</TableCell>
                                <TableCell className="text-xs">{row.branch || '-'}</TableCell>
                                <TableCell className="text-xs">
                                  {row.constituency ? (
                                    <Badge variant="outline" className="text-xs">
                                      {row.constituency}
                                    </Badge>
                                  ) : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {previewData.length > 20 && (
                          <p className="text-sm text-gray-500 text-center py-2">
                            ...and {previewData.length - 20} more students
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Progress */}
                {isUploading && (
                  <Card className="border-2 border-amber-200">
                    <CardContent className="py-6">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Importing students...</span>
                          <span className="text-sm text-gray-500">{uploadProgress}%</span>
                        </div>
                        <Progress value={uploadProgress} className="h-3" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Import Result */}
                {importResult && !importResult.error && (
                  <Card className={`border-2 ${replaceMode ? 'border-orange-300 bg-orange-50' : 'border-green-300 bg-green-50'}`}>
                    <CardContent className="py-6">
                      <div className="flex items-start gap-4">
                        <CheckCircle size={40} className={replaceMode ? 'text-orange-500' : 'text-green-500'} />
                        <div className="space-y-2 flex-1">
                          <h3 className={`font-semibold text-lg ${replaceMode ? 'text-orange-800' : 'text-green-800'}`}>
                            {replaceMode ? 'Replacement Successful!' : 'Import Successful!'}
                          </h3>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            {replaceMode && importResult.previous_enrollments_removed !== undefined && (
                              <div className="bg-white p-3 rounded-lg">
                                <p className="text-gray-500">Previous Removed</p>
                                <p className="text-2xl font-bold text-orange-600">{importResult.previous_enrollments_removed || 0}</p>
                              </div>
                            )}
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-gray-500">New Students</p>
                              <p className="text-2xl font-bold text-green-600">{importResult.new_users_created || importResult.created_users || 0}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-gray-500">Enrolled</p>
                              <p className="text-2xl font-bold text-blue-600">{importResult.total_enrolled || importResult.enrolled_in_school || 0}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-gray-500">Skipped</p>
                              <p className="text-2xl font-bold text-amber-600">{importResult.skipped || 0}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg">
                              <p className="text-gray-500">Errors</p>
                              <p className="text-2xl font-bold text-red-600">{importResult.errors?.length || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {importResult && importResult.error && (
                  <Card className="border-2 border-red-300 bg-red-50">
                    <CardContent className="py-6">
                      <div className="flex items-center gap-4">
                        <XCircle size={40} className="text-red-500" />
                        <div>
                          <h3 className="font-semibold text-red-800">Import Failed</h3>
                          <p className="text-red-600">{importResult.error}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Replace Mode Toggle */}
                <Card className={`border-2 ${replaceMode ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={replaceMode}
                            onChange={(e) => setReplaceMode(e.target.checked)}
                            className="sr-only peer"
                            data-testid="replace-mode-toggle"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                        <div>
                          <p className="font-medium text-gray-800">
                            {replaceMode ? 'Replace Mode (ON)' : 'Append Mode'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {replaceMode 
                              ? 'All existing enrollments for this school will be removed and replaced'
                              : 'New students will be added to existing enrollments'}
                          </p>
                        </div>
                      </div>
                      {replaceMode && (
                        <Badge variant="destructive" className="bg-orange-500">
                          <AlertTriangle size={12} className="mr-1" />
                          Destructive
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    onClick={handleBulkImport}
                    disabled={!bulkFile || !bulkSelectedSchool || isUploading || previewData.length === 0}
                    className={`flex-1 ${replaceMode 
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'}`}
                    size="lg"
                    data-testid="import-btn"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        {replaceMode ? 'Replacing...' : 'Importing...'}
                      </>
                    ) : (
                      <>
                        <Upload size={18} className="mr-2" />
                        {replaceMode ? `Replace with ${previewData.length} Students` : `Import ${previewData.length} Students`}
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleBulkReset}
                    disabled={isUploading}
                    size="lg"
                  >
                    <RefreshCw size={18} className="mr-2" />
                    Reset
                  </Button>
                </div>
              </div>

              {/* Right Column - Instructions */}
              <div className="space-y-6">
                {/* Template Download */}
                <Card className="border-2 border-amber-200">
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Download className="text-amber-500" />
                      Download Template
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Download a sample template to see the required format for student data.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={downloadTemplate}
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="download-template-btn"
                    >
                      <Download size={16} className="mr-2" />
                      Download CSV Template
                    </Button>
                  </CardContent>
                </Card>

                {/* Instructions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Instructions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-gray-600">
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      <p>Select the school where students will be enrolled</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      <p>Upload an Excel or CSV file with student data</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      <p>Review the preview to ensure data is correct</p>
                    </div>
                    <div className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      <p>Click "Import Students" to process the file</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Required Columns */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Required Columns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-green-500" />
                        <span><strong>Name</strong> - Full name</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle size={14} className="text-green-500" />
                        <span><strong>Email</strong> - Email address</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-[14px]" />
                        <span className="text-gray-500"><strong>Phone</strong> - Optional</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-[14px]" />
                        <span className="text-gray-500"><strong>Branch</strong> - Optional</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-[14px]" />
                        <span className="text-gray-500"><strong>Constituency</strong> - Optional</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Default Password Info */}
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-blue-800">Default Password</p>
                        <p className="text-blue-600 mt-1">
                          All imported students will be assigned the default password: <strong>Laikos2024</strong>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-6">
            {/* Header with count and actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <Clock className="text-amber-500" size={24} />
                  Pending Registrations
                </h2>
                <p className="text-sm text-gray-600">
                  Review and approve users who registered manually
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-base px-3 py-1">
                  {pendingUsers.length} Pending
                </Badge>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchPendingApprovals}
                  disabled={loadingApprovals}
                >
                  <RefreshCw size={16} className={loadingApprovals ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>

            {/* Search and Bulk Actions */}
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      placeholder="Search by name, email, branch..."
                      value={approvalSearchTerm}
                      onChange={(e) => setApprovalSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="approval-search-input"
                    />
                  </div>
                  
                  {selectedPendingUsers.length > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">
                        {selectedPendingUsers.length} selected
                      </span>
                      <Button 
                        onClick={handleBulkApprove}
                        className="bg-green-600 hover:bg-green-700"
                        disabled={processingUserIds.length > 0}
                        data-testid="bulk-approve-btn"
                      >
                        {processingUserIds.length > 0 ? (
                          <Loader2 size={16} className="animate-spin mr-2" />
                        ) : (
                          <CheckCircle size={16} className="mr-2" />
                        )}
                        Approve Selected
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Pending Users List */}
            {loadingApprovals ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={32} className="animate-spin text-[#5f7c8a]" />
              </div>
            ) : filteredPendingUsers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    No Pending Approvals
                  </h3>
                  <p className="text-gray-600">
                    All user registrations have been reviewed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Select All Header */}
                <div className="flex items-center gap-3 px-2">
                  <Checkbox
                    checked={selectedPendingUsers.length === filteredPendingUsers.length && filteredPendingUsers.length > 0}
                    onCheckedChange={toggleSelectAllPending}
                    data-testid="select-all-pending"
                  />
                  <span className="text-sm text-gray-600">Select All</span>
                </div>

                {/* User Cards */}
                {filteredPendingUsers.map((user) => (
                  <Card 
                    key={user.id} 
                    className={`hover:shadow-md transition-shadow ${
                      selectedPendingUsers.includes(user.id) ? 'ring-2 ring-[#5f7c8a]' : ''
                    }`}
                    data-testid={`pending-user-${user.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <Checkbox
                          checked={selectedPendingUsers.includes(user.id)}
                          onCheckedChange={() => toggleSelectPendingUser(user.id)}
                          className="mt-1"
                        />

                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h3 className="font-semibold text-gray-800">{user.name}</h3>
                            <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                              <Mail size={12} />
                              {user.email}
                            </div>
                            {user.phone && (
                              <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                                <Phone size={12} />
                                {user.phone}
                              </div>
                            )}
                          </div>

                          <div>
                            {user.branch && (
                              <div className="flex items-center gap-2 text-gray-600 text-sm">
                                <Building size={12} />
                                <span className="font-medium">Branch:</span> {user.branch}
                              </div>
                            )}
                            {user.constituency && (
                              <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                                <MapPin size={12} />
                                <span className="font-medium">Constituency:</span> {user.constituency}
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-500 text-xs mt-2">
                              <Clock size={12} />
                              {formatDate(user.created_at)}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(user.id)}
                              className="bg-green-600 hover:bg-green-700"
                              disabled={processingUserIds.includes(user.id)}
                              data-testid={`approve-${user.id}`}
                            >
                              {processingUserIds.includes(user.id) ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle size={14} className="mr-1" />
                                  Approve
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleRejectUser(user.id)}
                              disabled={processingUserIds.includes(user.id)}
                              data-testid={`reject-${user.id}`}
                            >
                              {processingUserIds.includes(user.id) ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <XCircle size={14} className="mr-1" />
                                  Reject
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Assign Students Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assign Students to Teacher</DialogTitle>
              <DialogDescription>
                Select a school and choose students to assign
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* School Selection */}
              <div>
                <Label>Select School</Label>
                <Select value={assignSchoolId} onValueChange={handleSchoolChange}>
                  <SelectTrigger data-testid="assign-school-select">
                    <SelectValue placeholder="Choose a school" />
                  </SelectTrigger>
                  <SelectContent>
                    {schools.map(school => (
                      <SelectItem key={school.id} value={school.id}>
                        <div className="flex items-center gap-2">
                          <School size={14} />
                          {school.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Students List */}
              {assignSchoolId && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Select Students</Label>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={selectAllStudents}>
                        Select All Available
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setSelectedStudents([])}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {loadingStudents ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="animate-spin text-gray-400" size={24} />
                    </div>
                  ) : filteredStudents.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No students enrolled in this school</p>
                  ) : (
                    <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                      {/* Available students group */}
                      {filteredStudents.filter(s => !s.assigned_teacher_id).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-green-50 border-b text-xs font-semibold text-green-700 uppercase tracking-wide sticky top-0">
                            Available to Assign ({filteredStudents.filter(s => !s.assigned_teacher_id).length})
                          </div>
                          {filteredStudents.filter(s => !s.assigned_teacher_id).map(student => (
                            <div 
                              key={student.id}
                              className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={selectedStudents.includes(student.id)}
                                  onCheckedChange={() => toggleStudentSelection(student.id)}
                                />
                                <div>
                                  <p className="font-medium">{student.name}</p>
                                  <p className="text-sm text-gray-500">{student.email}</p>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-green-600">Available</Badge>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Assigned students group */}
                      {filteredStudents.filter(s => s.assigned_teacher_id).length > 0 && (
                        <>
                          <div className="px-3 py-2 bg-gray-100 border-b text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0">
                            Already Assigned ({filteredStudents.filter(s => s.assigned_teacher_id).length})
                          </div>
                          {filteredStudents.filter(s => s.assigned_teacher_id).map(student => (
                            <div 
                              key={student.id}
                              className="flex items-center justify-between p-3 border-b last:border-b-0 bg-gray-50 opacity-70"
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={false}
                                  disabled={true}
                                />
                                <div>
                                  <p className="font-medium text-gray-600">{student.name}</p>
                                  <p className="text-sm text-gray-400">{student.email}</p>
                                </div>
                              </div>
                              <Badge variant="secondary">
                                Assigned to {student.assigned_teacher_name || 'Unknown'}
                              </Badge>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Count & Submit */}
              {assignSchoolId && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-gray-600">
                    {selectedStudents.length} student(s) selected
                  </span>
                  <Button 
                    onClick={handleAssignStudents}
                    disabled={assigning || selectedStudents.length === 0}
                  >
                    {assigning ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                    Assign Selected Students
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>

      <Footer />
    </div>
  );
};

const TeacherManagementPage = () => {
  return (
    <AdminProtected>
      <TeacherManagementContent />
    </AdminProtected>
  );
};

export default TeacherManagementPage;
